import { createReducer, createAction } from '@reduxjs/toolkit';

import { AntennaManager, WebSocketManager, Loadable } from 'models';
import { makeThunkFromAPICall } from 'state';

export type AntennasState = {
    list: Loadable<AntennaManager.Antenna[]>;
    lastAntennaUpdate?: WebSocketManager.UpdateAntennaStatusCommand;
    lastGroundStationUpdate?: WebSocketManager.GroundStationUpdateStatusCommand;
};

const initialState: AntennasState = {
    list: { status: 'idle' },
};

export const [getAntennaList, setAntennaList] = makeThunkFromAPICall(
    AntennaManager.listAntennas,
    'Antennas/list',
);

export const [getAntenna, setAntenna] = makeThunkFromAPICall(
    AntennaManager.getAntenna,
    'Antennas/get',
);

export const updateAntennaPosition =
    createAction<WebSocketManager.UpdateAntennaPositionCommand>(
        'Antennas/update-position',
    );

export const updateAntennaStatus =
    createAction<WebSocketManager.UpdateAntennaStatusCommand>(
        'Antennas/update',
    );

export const updateAntennaConnected =
    createAction<WebSocketManager.GroundStationUpdateStatusCommand>(
        'Antennas/update-groundstation-connected',
    );

export const antennasReducer = createReducer(initialState, (builder) => {
    builder.addCase(setAntennaList, (state, action) => {
        return {
            ...state,
            list: { ...state.list, ...action.payload.loadable },
        };
    });

    builder.addCase(updateAntennaConnected, (state, { payload }) => {
        return {
            ...state,
            list: {
                ...state.list,
                object: state.list.object?.map((antenna) => {
                    if (payload.id !== antenna.groundstation.id) return antenna;

                    return {
                        ...antenna,
                        groundstation: {
                            ...antenna.groundstation,
                            connectionId: payload.connection_id,
                        },
                    };
                }),
            },
            lastGroundStationUpdate: payload,
        };
    });

    builder.addCase(updateAntennaStatus, (state, { payload }) => {
        return {
            ...state,
            list: {
                ...state.list,
                object: state.list.object?.map((antenna) => {
                    if (payload.antenna_id !== antenna.id) return antenna;

                    return {
                        ...antenna,
                        status: payload.status,
                    };
                }),
            },
            lastAntennaUpdate: payload,
        };
    });

    builder.addCase(updateAntennaPosition, (state, { payload }) => {
        return {
            ...state,
            list: {
                ...state.list,
                object: state.list.object?.map((antenna) => {
                    if (payload.antenna_id !== antenna.id) return antenna;

                    return {
                        ...antenna,
                        lastKnownAzimuth: payload.azimuth,
                        lastKnownElevation: payload.elevation,
                        lastKnownTime: new Date(payload.time),
                    };
                }),
            },
        };
    });
});

export const antennasActions = {
    getAntennaList,
    getAntenna,
    setAntennaList,
    updateAntennaPosition,
    updateAntennaStatus,
    updateAntennaConnected,
};

export default antennasReducer;
